// Read-only evidence comparison for the original Customer Import template.
// Never infer that a name/email pair is verified simply because it appears in
// an external workbook: the saved customer/project impact checks still govern
// any actual edit or merge.
export const sourceKey = (value) => String(value ?? "")
  .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase().replace(/[^a-z0-9]/g, "");

export const sourceEmail = (value) => String(value ?? "").trim().toLowerCase();

export const validSourceEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sourceEmail(value));

const fullKey = (entry, first, last) =>
  sourceKey(`${entry[first] || ""} ${entry[last] || ""}`);

export function analyzeCustomerContactSource(contacts = [], sourceRows = []) {
  const sourceByName = new Map();
  for (const row of sourceRows) {
    const key = fullKey(row, "First Name *", "Last Name");
    if (!key) continue;
    if (!sourceByName.has(key)) sourceByName.set(key, []);
    sourceByName.get(key).push(row);
  }

  const ownerNamesPerSourceEmail = new Map();
  for (const row of sourceRows) {
    const email = sourceEmail(row["Email *"]);
    const name = fullKey(row, "First Name *", "Last Name");
    if (!validSourceEmail(email) || !name) continue;
    if (!ownerNamesPerSourceEmail.has(email)) ownerNamesPerSourceEmail.set(email, new Set());
    ownerNamesPerSourceEmail.get(email).add(name);
  }

  const ownersByEmail = new Map();
  for (const contact of contacts) {
    const email = sourceEmail(contact.email);
    if (!email) continue;
    if (!ownersByEmail.has(email)) ownersByEmail.set(email, []);
    ownersByEmail.get(email).push(contact);
  }

  const candidates = contacts
    .filter((contact) => contact.kind === "name-only" && !contact.mergedIntoContactId)
    .map((contact) => {
      const sourceRecords = sourceByName.get(fullKey(contact, "firstName", "lastName")) || [];
      const emails = [...new Set(sourceRecords.map((r) => sourceEmail(r["Email *"]))
        .filter(validSourceEmail))];
      const titleEmail = validSourceEmail(contact.title) ? sourceEmail(contact.title) : "";
      const suspiciousName = /^(?:dn[emc]\d+|gst|provincial)$/i.test(
        String(contact.firstName || "").trim()) ||
        /(?:accounts payable|help desk|non po|do not remove)/i.test(
          `${contact.firstName} ${contact.lastName}`);
      const email = emails.length === 1 ? emails[0] : "";
      const owners = email ? ownersByEmail.get(email) || [] : [];
      const sharedEmail = email && (ownerNamesPerSourceEmail.get(email)?.size || 0) > 1;
      const state = emails.length > 1 ? "source-conflict" :
        !email ? "no-source-email" :
        sharedEmail ? "source-shared-email" :
        suspiciousName ? "review-source-identity" :
        owners.length ? "email-already-in-core" : "email-not-in-core";
      return {
        contactId: contact.contactId,
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        name: [contact.firstName, contact.lastName].filter(Boolean).join(" "),
        usageCount: contact.usage?.count || 0,
        email,
        sourceCount: sourceRecords.length,
        sourceEmails: emails,
        ownerCount: owners.length,
        owners: owners.map((owner) => ({
          contactId: owner.contactId,
          name: [owner.firstName, owner.lastName].filter(Boolean).join(" "),
          kind: owner.kind,
          historical: Boolean(owner.mergedIntoContactId),
        })),
        titleEmail,
        state,
      };
    });

  const count = (state) => candidates.filter((candidate) => candidate.state === state).length;
  return {
    candidates,
    stats: {
      nameOnlyChecked: candidates.length,
      exactSourceEmail: candidates.filter((item) => item.sourceEmails.length === 1).length,
      emailAlreadyInCore: count("email-already-in-core"),
      emailNotInCore: count("email-not-in-core"),
      ambiguousOrSuspicious: count("source-conflict") + count("source-shared-email") +
        count("review-source-identity"),
      noSourceEmail: count("no-source-email"),
      misplacedTitleEmail: candidates.filter((item) => item.titleEmail).length,
    },
  };
}
